import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, catchError, of, switchMap, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { UserService } from '../user/user.service';
import { Role, User } from '../user/user.types';
import { navigationConfig } from './navigation.config';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    user$?: Observable<User | null>;
    currentUser?: User;
    userRole?: Role;
    modules: string[] = [];

    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private userService: UserService,
        private authService: AuthService,
    ) {
        this.user$ = this.authService.user$;
        this.user$.subscribe((user) => {
            if (user) {
                this.currentUser = user;
                this.userRole = user.role;
                this.updateModules(user);
            }
        });
    }

    /**
     * Met à jour la liste des modules activés pour l'utilisateur
     */
    private updateModules(user: User): void {
        // Reset modules array to avoid duplicates on refresh
        this.modules = [];
        if (user._company) {
            if (user._company.congesOption) this.modules.push('conges');
            if (user._company.interventionsOption) this.modules.push('interventions');
            if (user._company.contratsOption) this.modules.push('contrats');
            if (user._company.prepaieOption) this.modules.push('prepaie');
            if (user._company.statistiquesOption) this.modules.push('statistiques');
        }
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Vérifie si l'utilisateur peut gérer les congés (pour TeamAndSiteLeader)
     */
    private checkCanManageConges(): Observable<boolean> {
        return this._httpClient.get<{ canManage: boolean }>('/api/conges/can-manage').pipe(
            switchMap(response => of(response.canManage)),
            catchError(() => of(false))
        );
    }

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        // Filtre initial par rôle
        let filteredNavigationConfig = navigationConfig.filter(item => {
            if (item.roles) {
                return item.roles.includes(this.userRole!);
            }
            return true;
        });

        // Filtre par modules et prepaie managers
        filteredNavigationConfig = filteredNavigationConfig.filter((item: any) => {
            if (item.id === 'prepaie') {
                const managers = this.currentUser?._company?.prepaieManagers;
                const userId = this.currentUser?._id;
                // Afficher seulement si l'utilisateur est listé comme manager
                if (!Array.isArray(managers) || !userId) return false;
                return managers.includes(userId);
            }

            if (item.modules && Array.isArray(item.modules)) {
                return item.modules.some((module: string) => this.modules.includes(module));
            }
            return true;
        });

        // Pour Chef et Responsable, vérifier dynamiquement l'accès à la gestion des congés
        const needsCongesCheck = (this.userRole === Role.TeamLeader) &&
            filteredNavigationConfig.some((item: any) => item.requiresCongesManagerCheck);

        if (needsCongesCheck) {
            return this.checkCanManageConges().pipe(
                switchMap(canManage => {
                    if (!canManage) {
                        // Retirer l'élément gestion-conges si l'utilisateur n'a pas les droits
                        filteredNavigationConfig = filteredNavigationConfig.filter(
                            (item: any) => !item.requiresCongesManagerCheck
                        );
                    }
                    return of({
                        compact: filteredNavigationConfig,
                        default: filteredNavigationConfig,
                        futuristic: filteredNavigationConfig,
                        horizontal: filteredNavigationConfig,
                    } as Navigation);
                }),
                tap((navigation) => this._navigation.next(navigation))
            );
        }

        return of({
            compact: filteredNavigationConfig,
            default: filteredNavigationConfig,
            futuristic: filteredNavigationConfig,
            horizontal: filteredNavigationConfig,
        } as Navigation).pipe(tap((navigation) => this._navigation.next(navigation)));
    }
}
